import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NeoActionRouter, NeoDetailView, neoUi, NeoImmersiveItemIcon } from './NeoActionViews';
import { NeoIonicons } from './NeoIonicons';
import { getActionDef } from '../../data/immersiveData';

/** Mobile `ImmersiveExperience.js` orbIcon (58) / actionCard (62) */
const MODULE_SIZE = 58;
const MODULE_OFFSET = MODULE_SIZE / 2;
const ACTION_CHIP = 62;
const ACTION_OFFSET = ACTION_CHIP / 2;
const HUB_PX = 114;

/** Mobile Neo C + SETTINGS tints */
const A = {
  primary: '#6366f1',
  primaryLight: '#818cf8',
  warning: '#f59e0b',
  secondary: '#06b6d4',
  success: '#10b981',
  accent: '#ec4899',
};
const C = {
  ...A,
  dark: '#050505',
  textMuted: 'rgba(255,255,255,0.6)',
  textDim: 'rgba(255,255,255,0.4)',
  card: '#111111',
  cardBorder: 'rgba(255,255,255,0.06)',
  inputBg: 'rgba(255,255,255,0.04)',
  inputBorder: 'rgba(255,255,255,0.1)',
};

/**
 * Matches React Native `color + '40'` / `color + '15'` (8-digit #RRGGBBAA).
 * CSS 8-digit hex is inconsistent across engines; rgba is identical to RN.
 * @param {string} hex - #RRGGBB
 * @param {string} aa - two hex chars, e.g. '40' (border) or '15' (fill)
 */
function neoShell(hex, aa) {
  const h = (hex || '#6366f1').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = parseInt(aa, 16) / 255;
  return `rgba(${r},${g},${b},${a})`;
}

/** RN 1.5dp reads heavier on phones; ~3px on web matches perceived weight. */
const NEO_CHIP_BORDER_PX = 3;

/**
 * ImmersiveExperience4 `vividIconShell`: thick border in full color, fill `color+55` dark / `+2E` light.
 */
function neoVividChipStyle(hex, isDark) {
  return {
    boxSizing: 'border-box',
    borderRadius: 20,
    borderWidth: NEO_CHIP_BORDER_PX,
    borderStyle: 'solid',
    borderColor: hex,
    backgroundColor: neoShell(hex, isDark ? '55' : '2E'),
    overflow: 'hidden',
  };
}

/**
 * Webpack HMR can briefly serve a patch that still calls `NeoFilledLucide` after it was removed.
 * Keeps runtime stable; prefer `NeoIonicons` everywhere. If a Lucide component is passed (cached ref), render it.
 */
function NeoFilledLucide({ icon: Icon, size = 24, color = '#fff' }) {
  if (typeof Icon === 'function') {
    return (
      <Icon
        size={size}
        color={color}
        fill={color}
        stroke={color}
        strokeWidth={size >= 24 ? 2.05 : 1.85}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }
  return <NeoIonicons name="ellipse-outline" size={size} color={color} filled />;
}

// Aliases for older HMR bundles / any stray references (same as A.*)
const A_PRIMARY = A.primary;
const A_PRIMARY_LIGHT = A.primaryLight;

/**
 * Home categories — aligned with Expo `ImmersiveExperience4.js` (CATEGORIES + ACTIONS_BY_CAT).
 * Procurement is amber `#f59e0b`; chips use vividIconShell (thick border + saturated fill).
 */
const MODULES = [
  {
    neoId: 'supplychain',
    label: 'Supply Chain',
    ionIcon: 'layers-outline',
    color: '#6366f1',
    glowColor: 'rgba(99,102,241,0.15)',
    actions: [
      { id: 'create-po', label: 'Create PO', ionIcon: 'document-text-outline', color: '#6366f1' },
      { id: 'approve-po', label: 'Approvals', ionIcon: 'checkmark-circle-outline', color: '#10b981' },
      { id: 'view-orders', label: 'Orders', ionIcon: 'list-outline', color: '#06b6d4' },
      { id: 'track-shipment', label: 'Shipments', ionIcon: 'location-outline', color: '#ec4899' },
    ],
  },
  {
    neoId: 'maintenance',
    label: 'Maintenance',
    ionIcon: 'construct-outline',
    color: '#3b82f6',
    glowColor: 'rgba(59,130,246,0.15)',
    actions: [
      { id: 'new-request', label: 'New Request', ionIcon: 'add-circle-outline', color: '#3b82f6' },
      { id: 'view-tickets', label: 'Tickets', ionIcon: 'list-outline', color: '#6366f1' },
      { id: 'schedule', label: 'Schedule', ionIcon: 'calendar-outline', color: '#06b6d4' },
      { id: 'reports', label: 'Reports', ionIcon: 'bar-chart-outline', color: '#ec4899' },
    ],
  },
  {
    neoId: 'logistics',
    label: 'Logistics',
    ionIcon: 'car-outline',
    color: '#06b6d4',
    glowColor: 'rgba(6,182,212,0.15)',
    actions: [
      { id: 'new-delivery', label: 'Delivery', ionIcon: 'car-outline', color: '#06b6d4' },
      { id: 'track-fleet', label: 'Fleet', ionIcon: 'location-outline', color: '#6366f1' },
      { id: 'routes', label: 'Routes', ionIcon: 'map-outline', color: '#10b981' },
      { id: 'drivers', label: 'Drivers', ionIcon: 'people-outline', color: '#ec4899' },
    ],
  },
  {
    neoId: 'assets',
    label: 'Assets',
    ionIcon: 'business-outline',
    color: '#ec4899',
    glowColor: 'rgba(236,72,153,0.15)',
    actions: [
      { id: 'add-asset', label: 'Add Asset', ionIcon: 'add-circle-outline', color: '#ec4899' },
      { id: 'scan-asset', label: 'Scan QR', ionIcon: 'qr-code-outline', color: '#6366f1' },
      { id: 'view-assets', label: 'Assets', ionIcon: 'business-outline', color: '#06b6d4' },
      { id: 'asset-maintenance', label: 'Maint.', ionIcon: 'construct-outline', color: '#3b82f6' },
    ],
  },
  {
    neoId: 'inventory',
    label: 'Inventory',
    ionIcon: 'cube-outline',
    color: '#10b981',
    glowColor: 'rgba(16,185,129,0.15)',
    actions: [
      { id: 'add-item', label: 'Add Item', ionIcon: 'add-circle-outline', color: '#10b981' },
      { id: 'scan-barcode', label: 'Scan', ionIcon: 'barcode-outline', color: '#6366f1' },
      { id: 'stock-check', label: 'Stock', ionIcon: 'checkmark-circle-outline', color: '#06b6d4' },
      { id: 'inv-reports', label: 'Reports', ionIcon: 'bar-chart-outline', color: '#ec4899' },
    ],
  },
  {
    neoId: 'procurement',
    label: 'Procurement',
    ionIcon: 'cart-outline',
    color: '#f59e0b',
    glowColor: 'rgba(245,158,11,0.15)',
    actions: [
      { id: 'create-rfq', label: 'Create RFQ', ionIcon: 'document-text-outline', color: '#f59e0b' },
      { id: 'vendors', label: 'Vendors', ionIcon: 'people-outline', color: '#6366f1' },
      { id: 'quotes', label: 'Quotes', ionIcon: 'receipt-outline', color: '#06b6d4' },
      { id: 'proc-orders', label: 'Orders', ionIcon: 'cart-outline', color: '#10b981' },
    ],
  },
  {
    neoId: 'pulse',
    label: 'Pulse',
    ionIcon: 'pulse-outline',
    color: '#f43f5e',
    glowColor: 'rgba(244,63,94,0.14)',
    actions: [
      { id: 'pulse-tab-overview', label: 'Overview', ionIcon: 'pulse-outline', color: '#f43f5e' },
      { id: 'pulse-tab-analytics', label: 'Analytics', ionIcon: 'pie-chart-outline', color: '#ec4899' },
      { id: 'pulse-tab-map', label: 'Map', ionIcon: 'map-outline', color: '#f472b6' },
      { id: 'pulse-tab-comparison', label: 'Compare', ionIcon: 'bar-chart-outline', color: '#e11d48' },
      { id: 'pulse-tab-rankings', label: 'Rankings', ionIcon: 'ribbon-outline', color: '#a855f7' },
      { id: 'pulse-tab-performance', label: 'Performance', ionIcon: 'trending-up-outline', color: '#fb7185' },
    ],
  },
];

/** Quick actions — distinct tints (mobile-style palette, not all indigo) */
/** Same `QUICK_REQUESTS` icons/names as `ImmersiveExperience4.js` */
const QUICK_REQUESTS = [
  { id: 'qr-maintenance', label: 'Maintenance', ionIcon: 'construct', color: '#3b82f6', actionId: 'new-request' },
  { id: 'qr-purchase', label: 'Purchase', ionIcon: 'cart', color: '#10b981', actionId: 'create-po' },
  { id: 'qr-it', label: 'IT Support', ionIcon: 'desktop', color: '#06b6d4', actionId: 'it-tickets' },
  { id: 'qr-logistics', label: 'Logistics', ionIcon: 'car', color: '#8b5cf6', actionId: 'new-delivery' },
  { id: 'qr-safety', label: 'Safety', ionIcon: 'shield', color: '#ef4444', actionId: 'safety-incidents' },
  { id: 'qr-hr', label: 'HR Request', ionIcon: 'people', color: '#f59e0b', actionId: 'hr-leave' },
];

const SCAN_ORBIT = [
  { id: 'scan-asset', label: 'QR Code', ionIcon: 'qr-code-outline', color: A.primary },
  { id: 'scan-barcode', label: 'Barcode', ionIcon: 'barcode-outline', color: A.secondary },
];

function settingsOrbitItems(isDark) {
  return [
    { id: 'theme', label: isDark ? 'Light' : 'Dark', ionIcon: isDark ? 'sunny' : 'moon', color: isDark ? A.warning : A.primary },
    { id: 'profile', label: 'Profile', ionIcon: 'person-outline', color: A.primary },
    { id: 'activity', label: 'Activity', ionIcon: 'time-outline', color: A.secondary },
    { id: 'search', label: 'Search', ionIcon: 'search-outline', color: A.success },
    { id: 'customize', label: 'Customize', ionIcon: 'apps-outline', color: A.accent },
  ];
}

const RECENT_ACTIVITY = [
  { id: 'r1', title: 'PO-2024-001 shipped via Maersk', mod: 'Supply Chain', time: '10m ago' },
  { id: 'r2', title: 'MT-003 Parking Lights resolved', mod: 'Maintenance', time: '1h ago' },
  { id: 'r3', title: 'Truck A-201 departed Route 5', mod: 'Logistics', time: '2h ago' },
];

function OrbitalNode({ item, index, total, radius, onClick, variant, isDark }) {
  const angle = ((index * (360 / total)) - 90) * (Math.PI / 180);
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;
  const ionIcon = item.ionIcon ?? 'document-text-outline';
  const color = item.color ?? '#6366f1';
  const isAction = variant === 'action';
  const off = isAction ? ACTION_OFFSET : MODULE_OFFSET;
  /** Mobile st.orbLbl / st.actionName */
  const lblMuted = isDark ? C.textMuted : 'rgba(0,0,0,0.55)';
  const iconSize = isAction ? 22 : 24;

  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(item)}
      className="absolute left-1/2 top-1/2 cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-white/20 rounded-[20px]"
      style={{ x: x - off, y: y - off }}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.32 }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.97 }}
    >
      <div className="flex flex-col items-center">
        <div
          className={`flex items-center justify-center overflow-hidden pointer-events-none ${isAction ? 'h-[62px] w-[62px]' : 'h-[58px] w-[58px]'}`}
          style={neoVividChipStyle(color, isDark)}
        >
          <NeoIonicons name={ionIcon} size={iconSize} color={color} filled />
        </div>
        <span
          className={`text-center leading-tight pointer-events-none uppercase font-semibold ${
            isAction ? 'text-[9px] max-w-[72px] mt-[5px]' : 'text-[9px] max-w-[80px] mt-2 tracking-[0.1em]'
          }`}
          style={{ color: lblMuted }}
        >
          {item.label}
        </span>
      </div>
    </motion.button>
  );
}

const ORBITAL_SIZE = 560;

export default function LyncsNeoImmersive() {
  const [tab, setTab] = useState('home');
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [isDark, setIsDark] = useState(true);
  const [panel, setPanel] = useState(null);
  /** Third level: list-type actions open a floating panel; orbit shifts left */
  const [orbitListOverlay, setOrbitListOverlay] = useState(null);
  const [orbitListSelected, setOrbitListSelected] = useState(null);
  /** Stays true until list panel exit finishes — avoids layout snapping before slide-out */
  const [orbitListLayoutOpen, setOrbitListLayoutOpen] = useState(false);
  const [mdUp, setMdUp] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const fn = () => setMdUp(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  useEffect(() => {
    setOrbitListOverlay(null);
    setOrbitListSelected(null);
    /* orbitListLayoutOpen: cleared in onExitComplete when list panel animates out */
  }, [selectedModule]);

  const glowTop = useMemo(() => {
    if (showQuickMenu) return 'rgba(99,102,241,0.16)';
    if (selectedModule?.glowColor) return selectedModule.glowColor;
    /** Mobile default LinearGradient top stop */
    return isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.06)';
  }, [showQuickMenu, selectedModule, isDark]);

  const pulseTint = showQuickMenu ? A.primary : selectedModule?.color || A.primary;

  const orbitalConfig = useMemo(() => {
    if (showQuickMenu) {
      return { items: QUICK_REQUESTS, radius: 210, variant: 'quick' };
    }
    if (tab === 'scan') {
      return { items: SCAN_ORBIT, radius: 200, variant: 'scan' };
    }
    if (tab === 'settings') {
      return { items: settingsOrbitItems(isDark), radius: 210, variant: 'settings' };
    }
    if (selectedModule) {
      return {
        items: selectedModule.actions,
        radius: 220,
        variant: 'action',
      };
    }
    if (tab === 'home') {
      return {
        items: MODULES,
        radius: 238,
        variant: 'module',
      };
    }
    return { items: [], radius: 250, variant: 'none' };
  }, [tab, selectedModule, showQuickMenu, isDark]);

  const { items: orbitalItems, radius, variant: orbitVariant } = orbitalConfig;

  const openAction = useCallback((actionId, color) => {
    setShowQuickMenu(false);
    setSelectedModule(null);
    setPanel(null);
    setActiveView({ actionId, color });
  }, []);

  const handleOrbitalClick = useCallback(
    (item) => {
      if (showQuickMenu) {
        openAction(item.actionId, item.color);
        return;
      }
      if (tab === 'scan') {
        openAction(item.id, item.color);
        return;
      }
      if (tab === 'settings') {
        if (item.id === 'theme') {
          setIsDark((d) => !d);
          return;
        }
        if (item.id === 'profile') {
          setPanel('profile');
          return;
        }
        if (item.id === 'activity') {
          setPanel('activity');
          return;
        }
        if (item.id === 'search') {
          setPanel('search');
          return;
        }
        if (item.id === 'customize') {
          setPanel('customize');
          return;
        }
        return;
      }
      if (selectedModule) {
        const def = getActionDef(item.id);
        if (def?.type === 'list' && Array.isArray(def.items) && def.items.length > 0) {
          setOrbitListSelected(null);
          setOrbitListLayoutOpen(true);
          setOrbitListOverlay({
            actionId: item.id,
            color: item.color ?? selectedModule.color,
            title: def.title,
            ionIcon: item.ionIcon,
            items: def.items,
            listActions: def.actions,
          });
          return;
        }
        openAction(item.id, item.color ?? selectedModule.color);
        return;
      }
      const mod = MODULES.find((m) => m.neoId === item.neoId);
      if (mod) setSelectedModule(mod);
    },
    [showQuickMenu, tab, selectedModule, openAction],
  );

  const closeOrbitListPanel = useCallback(() => {
    setOrbitListSelected(null);
    setOrbitListOverlay(null);
  }, []);

  const handleOrbitListExitComplete = useCallback(() => {
    setOrbitListLayoutOpen(false);
  }, []);

  const goBack = useCallback(() => {
    if (activeView) {
      setActiveView(null);
      return;
    }
    if (panel) {
      setPanel(null);
      return;
    }
    if (orbitListSelected) {
      setOrbitListSelected(null);
      return;
    }
    if (orbitListOverlay) {
      closeOrbitListPanel();
      return;
    }
    if (showQuickMenu) {
      setShowQuickMenu(false);
      return;
    }
    if (selectedModule) {
      setSelectedModule(null);
      return;
    }
    if (tab === 'scan' || tab === 'settings') {
      setTab('home');
    }
  }, [activeView, panel, orbitListOverlay, orbitListSelected, showQuickMenu, selectedModule, tab, closeOrbitListPanel]);

  const handleCenterPress = useCallback(() => {
    setShowQuickMenu((v) => !v);
    setSelectedModule(null);
    setOrbitListOverlay(null);
    setOrbitListSelected(null);
    /* layout stays true until list panel exit completes (onExitComplete) */
  }, []);

  const showBack =
    !!orbitListLayoutOpen ||
    !!orbitListOverlay ||
    !!selectedModule ||
    showQuickMenu ||
    tab === 'scan' ||
    tab === 'settings' ||
    (tab === 'modules' && !!selectedModule);

  const bgClass = isDark ? 'bg-[#050505]' : 'bg-[#f2f2f7]';
  const textMuted = isDark ? 'text-white/60' : 'text-black/55';
  const neoHeaderMuted = isDark ? 'text-[rgba(255,255,255,0.4)]' : 'text-black/45';
  const navBarBg = isDark ? 'bg-[rgba(12,12,14,0.96)] border-white/[0.08]' : 'bg-white/95 border-black/10';
  const ringBorder = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  /** Same as Expo `BottomNav`: one primary tint for every active tab (not per-route colors). */
  const navInactiveIcon = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)';
  const navActiveBg = 'rgba(99,102,241,0.12)';

  /** Layout split + orbit shift (decoupled from overlay null during exit animation) */
  const showOrbitListShift = orbitListLayoutOpen;

  const listSpring = { type: 'spring', stiffness: 300, damping: 32, mass: 0.9 };
  const orbitShiftX = showOrbitListShift ? (mdUp ? -76 : -28) : 0;
  const orbitScale = showOrbitListShift ? 0.9 : 1;

  const listPanelUi = neoUi(isDark);

  if (activeView) {
    return (
      <div className="fixed inset-0 z-[200]">
        <NeoActionRouter
          actionId={activeView.actionId}
          color={activeView.color}
          isDark={isDark}
          onBack={() => setActiveView(null)}
        />
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen overflow-hidden ${bgClass} ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
      {/* Neo-style top glow — matches mobile LinearGradient */}
      <div
        className="absolute inset-0 pointer-events-none transition-[background] duration-500"
        style={{
          background: `linear-gradient(180deg, ${glowTop} 0%, transparent 55%)`,
        }}
      />
      <div
        className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(circle_at_80%_70%,rgba(6,182,212,0.08),transparent_42%)]' : 'bg-[radial-gradient(circle_at_80%_70%,rgba(6,182,212,0.05),transparent_42%)]'}`}
      />
      <div className={`absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] ${isDark ? 'opacity-[0.18]' : 'opacity-[0.08]'}`} />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          className={`absolute h-[900px] w-[900px] rounded-full border -translate-x-1/2 -translate-y-1/2 ${ringBorder} border-opacity-40`}
          style={{ left: '50%', top: '50%', borderColor: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.15)' }}
          animate={{ rotate: 360 }}
          transition={{ duration: 70, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className={`absolute h-[680px] w-[680px] rounded-full border -translate-x-1/2 -translate-y-1/2 ${ringBorder}`}
          style={{ left: '50%', top: '50%', borderColor: isDark ? 'rgba(6,182,212,0.1)' : 'rgba(6,182,212,0.12)' }}
          animate={{ rotate: -360 }}
          transition={{ duration: 55, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      <div className="relative z-10 flex h-screen flex-col pb-[100px]">
        <header
          className={`relative z-[100] flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0 ${isDark ? 'border-white/10 bg-black/25 backdrop-blur-xl' : 'border-black/10 bg-white/70 backdrop-blur-xl'}`}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {showBack ? (
          <button
            type="button"
            onClick={goBack}
            className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-[13px] border shrink-0 w-10 h-10 justify-center sm:w-auto sm:h-auto sm:px-3 ${isDark ? 'bg-white/[0.04] border-white/[0.08] hover:bg-white/10' : 'bg-black/[0.04] border-black/10'}`}
          >
                <NeoIonicons
                  name={selectedModule || orbitListOverlay || orbitListLayoutOpen ? 'chevron-back' : 'close'}
                  size={18}
                  color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)'}
                  filled
                />
                <span className="text-sm font-semibold hidden sm:inline">Back</span>
              </button>
            ) : (
              <div className="w-10 shrink-0 sm:w-[88px]" aria-hidden />
            )}
            <div className="min-w-0 text-center flex-1">
              <p className={`text-[12px] font-light tracking-[0.35em] uppercase ${neoHeaderMuted}`}>IMMERSIVE</p>
              <h1 className={`text-[11px] sm:text-xs font-normal mt-0.5 truncate ${textMuted}`}>
                {selectedModule
                  ? selectedModule.label
                  : showQuickMenu
                    ? 'Quick actions'
                    : tab === 'modules'
                      ? 'Modules'
                      : tab === 'scan'
                        ? 'Scan'
                        : tab === 'settings'
                          ? 'Settings'
                          : 'LYNCS NEO'}
              </h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPanel('profile')}
            className={`shrink-0 w-10 h-10 rounded-[13px] border flex items-center justify-center ${isDark ? 'bg-white/[0.04] border-white/[0.08] hover:bg-white/10' : 'bg-black/[0.04] border-black/10'}`}
            aria-label="Profile"
          >
            <NeoIonicons
              name="person"
              size={16}
              color={A.primary}
              filled
            />
          </button>
        </header>

        {tab === 'modules' && !selectedModule ? (
          <main className={`flex-1 overflow-y-auto px-5 pt-4 ${isDark ? '' : 'bg-[#f2f2f7]'}`}>
            <p className={`text-[13px] font-semibold mb-4 ${textMuted}`}>
              {MODULES.length} categories — Neo hub
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5 sm:gap-3 pb-8">
              {MODULES.map((cat) => (
                <button
                  key={cat.neoId}
                  type="button"
                  onClick={() => setSelectedModule(cat)}
                  className={`relative flex flex-col items-center py-3.5 px-2 rounded-[18px] border transition-transform active:scale-[0.97] ${
                    isDark ? 'bg-white/[0.04] border-white/10 hover:bg-white/[0.07]' : 'bg-white border-black/10 shadow-sm hover:bg-black/[0.02]'
                  }`}
                >
                  <div className="w-[58px] h-[58px] flex items-center justify-center" style={neoVividChipStyle(cat.color, isDark)}>
                    <NeoIonicons name={cat.ionIcon} size={24} color={cat.color} filled />
                  </div>
                  <span className={`text-[11px] font-bold mt-2 text-center leading-tight line-clamp-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          </main>
        ) : (
          <main
            className={`flex-1 relative z-0 flex min-h-0 overflow-hidden ${
              showOrbitListShift ? 'mt-0 pt-2 md:pt-3 flex-col md:flex-row md:items-stretch' : '-mt-10 items-center justify-center'
            }`}
          >
            {/* Left: orbit — layout moves it left when list is open (no full-screen overlay) */}
            <div
              className={`relative flex min-h-0 overflow-visible ${
                showOrbitListShift
                  ? 'w-full md:w-1/2 md:max-w-[50%] flex-1 md:flex-none justify-center items-center md:justify-end md:pr-1 lg:pr-2 py-3 md:py-0 min-h-[240px] md:min-h-0 md:self-stretch'
                  : 'w-full justify-center items-center'
              }`}
            >
              <motion.div
                className="relative flex items-center justify-center w-full h-full min-h-[220px] will-change-transform"
                initial={false}
                animate={{ x: orbitShiftX, scale: orbitScale }}
                transition={listSpring}
              >
              {/* Mobile pulseRing + ring1 + ring2 (hub-relative) */}
              <motion.div
                className="absolute rounded-full pointer-events-none left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0"
                style={{
                  width: HUB_PX + 30,
                  height: HUB_PX + 30,
                  backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
                }}
                animate={{ opacity: [0.35, 0.7, 0.35] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              />
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none z-0"
                style={{
                  width: HUB_PX + 24,
                  height: HUB_PX + 24,
                  borderWidth: 1,
                  borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)',
                }}
              />
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none z-0"
                style={{
                  width: HUB_PX + 60,
                  height: HUB_PX + 60,
                  borderWidth: 1,
                  borderColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.04)',
                }}
              />
              <div className="relative z-[1]" style={{ width: ORBITAL_SIZE, height: ORBITAL_SIZE }}>
                <div
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border ${isDark ? 'border-white/15' : 'border-black/10'}`}
                  style={{ width: radius * 2, height: radius * 2 }}
                />

                <motion.div
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center px-1 z-10 overflow-hidden ${isDark ? '' : 'bg-white/95'}`}
                  style={{
                    width: HUB_PX,
                    height: HUB_PX,
                    borderWidth: 2.5,
                    borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.96)',
                  }}
                  animate={
                    isDark
                      ? {
                          boxShadow: [
                            `0 0 0 0 ${neoShell(pulseTint, '35')}`,
                            `0 0 0 14px ${neoShell(pulseTint, '00')}`,
                            `0 0 0 0 ${neoShell(pulseTint, '35')}`,
                          ],
                        }
                      : { boxShadow: '0 0 0 0 transparent' }
                  }
                  transition={isDark ? { duration: 2.6, repeat: Infinity, ease: 'easeOut' } : { duration: 0.2 }}
                >
                  {showQuickMenu && (
                    <div className="flex flex-col items-center gap-1.5">
                      <NeoIonicons name="add-circle" size={32} color={A.primary} filled />
                      <p className="text-[10px] font-extrabold tracking-[0.15em]" style={{ color: isDark ? C.textDim : 'rgba(0,0,0,0.45)' }}>
                        NEW
                      </p>
                    </div>
                  )}
                  {!showQuickMenu && tab === 'scan' && (
                    <div className="flex flex-col items-center gap-1.5">
                      <NeoIonicons name="scan" size={32} color={A.secondary} filled />
                      <p className="text-[10px] font-extrabold tracking-[0.15em]" style={{ color: isDark ? C.textDim : 'rgba(0,0,0,0.45)' }}>
                        SCAN
                      </p>
                    </div>
                  )}
                  {!showQuickMenu && tab === 'settings' && (
                    <>
                      <NeoIonicons name="settings" size={30} color={A.warning} filled />
                      <p className="text-[10px] font-extrabold tracking-[0.15em] mt-1" style={{ color: isDark ? C.textDim : 'rgba(0,0,0,0.45)' }}>
                        SETTINGS
                      </p>
                    </>
                  )}
                  {!showQuickMenu && !selectedModule && tab === 'home' && (
                    <div className="flex flex-col items-center gap-1.5">
                      <NeoIonicons name="flash" size={32} color="#7c3aed" filled />
                      <p className="text-[10px] font-extrabold tracking-[0.2em]" style={{ color: isDark ? C.textDim : 'rgba(0,0,0,0.45)' }}>
                        NEO
                      </p>
                    </div>
                  )}
                  {!showQuickMenu && selectedModule && (
                    <div className="flex flex-col items-center gap-1.5">
                      <NeoIonicons name={selectedModule.ionIcon} size={32} color={selectedModule.color} filled />
                      <p
                        className="text-[10px] font-extrabold text-center leading-tight max-w-[100px] uppercase tracking-wide"
                        style={{ color: isDark ? C.textDim : 'rgba(0,0,0,0.45)' }}
                      >
                        {selectedModule.label}
                      </p>
                    </div>
                  )}
                </motion.div>

                {orbitalItems.map((item, i) => (
                  <OrbitalNode
                    key={item.id || item.neoId}
                    item={item}
                    index={i}
                    total={orbitalItems.length}
                    radius={radius}
                    onClick={handleOrbitalClick}
                    variant={orbitVariant === 'action' || showQuickMenu || tab === 'scan' || tab === 'settings' ? 'action' : 'module'}
                    isDark={isDark}
                  />
                ))}
              </div>
              </motion.div>
            </div>

            {/* Right: in-screen list (half width on md+) — slides in, no blur overlay */}
            <AnimatePresence onExitComplete={handleOrbitListExitComplete}>
              {orbitListOverlay && (
                <motion.div
                  key="orbit-list-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="orbit-list-title"
                  initial={{ opacity: 0, x: 56 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 56 }}
                  transition={listSpring}
                  className={`relative z-[1] flex flex-col min-h-0 overflow-hidden flex-1 md:flex-none w-full md:w-1/2 md:max-w-[50%]
                    border-t md:border-t-0 md:border-l rounded-t-[28px] md:rounded-none md:rounded-l-[28px] md:shadow-[-12px_0_40px_rgba(0,0,0,0.2)]
                    max-h-[min(52vh,520px)] md:max-h-none md:self-stretch
                    ${isDark ? 'border-white/[0.1] bg-[#111111]' : 'border-black/10 bg-[#fafafa] shadow-[inset_1px_0_0_rgba(0,0,0,0.06)]'}`}
                >
                  {orbitListSelected ? (
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden h-full">
                      <NeoDetailView
                        item={orbitListSelected}
                        color={orbitListOverlay.color}
                        onBack={() => setOrbitListSelected(null)}
                        listActions={orbitListOverlay.listActions}
                        isDark={isDark}
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        className={`shrink-0 flex items-center gap-3 px-4 sm:px-5 py-4 border-b ${
                          isDark ? 'border-white/10 bg-[#141414]' : 'border-black/10 bg-[#f0f0f3]'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={closeOrbitListPanel}
                          className={`p-2 rounded-xl border shrink-0 ${
                            isDark
                              ? 'bg-white/[0.05] border-white/[0.1] hover:bg-white/10'
                              : 'bg-black/[0.04] border-black/10 hover:bg-black/[0.07]'
                          }`}
                          aria-label="Close"
                        >
                      <NeoIonicons
                        name="close"
                        size={20}
                        color={isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)'}
                        filled
                      />
                    </button>
                    <div className="flex-1 min-w-0 flex items-center gap-2.5">
                      {orbitListOverlay.ionIcon ? (
                        <div
                          className="w-10 h-10 rounded-[14px] flex items-center justify-center shrink-0"
                          style={neoVividChipStyle(orbitListOverlay.color, isDark)}
                        >
                          <NeoIonicons name={orbitListOverlay.ionIcon} size={20} color={orbitListOverlay.color} filled />
                        </div>
                      ) : null}
                      <h2 id="orbit-list-title" className={`text-base sm:text-lg font-bold tracking-wide truncate ${listPanelUi.title}`}>
                        {orbitListOverlay.title}
                      </h2>
                    </div>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-lg shrink-0 ${isDark ? 'text-white/45 bg-white/[0.06]' : 'text-black/45 bg-black/[0.05]'}`}
                    >
                      {orbitListOverlay.items.length}
                    </span>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4 space-y-3 pb-6">
                    {orbitListOverlay.items.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => setOrbitListSelected(row)}
                        className={`w-full flex items-start gap-4 p-4 sm:p-4 rounded-2xl border text-left transition-colors ${listPanelUi.listRow}`}
                      >
                        <div
                          className="p-2.5 rounded-[14px] border-[1.5px] shrink-0"
                          style={{
                            borderColor: orbitListOverlay.color,
                            backgroundColor: `${orbitListOverlay.color}22`,
                          }}
                        >
                          <NeoImmersiveItemIcon name={row.icon} color={orbitListOverlay.color} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-[15px] sm:text-base font-semibold truncate ${listPanelUi.title}`}>{row.title}</p>
                          <p className={`text-xs sm:text-sm truncate mt-1 ${listPanelUi.muted}`}>{row.subtitle}</p>
                        </div>
                        <div className="text-right shrink-0 max-w-[32%]">
                          {row.detail != null && (
                            <p className={`text-xs font-semibold ${isDark ? 'text-white/70' : 'text-black/65'}`}>{row.detail}</p>
                          )}
                          {row.status != null && (
                            <span
                              className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold"
                              style={{
                                backgroundColor: `${row.statusColor}22`,
                                color: row.statusColor,
                              }}
                            >
                              {row.status}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </main>
        )}
      </div>

      {/* Bottom nav — mobile Neo layout: pill bar + elevated FAB */}
      <div className="fixed bottom-0 left-0 right-0 z-[80] flex flex-col items-center pointer-events-none">
        <div className="relative w-full max-w-[340px] px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-auto">
          <div className={`flex flex-row items-center rounded-[32px] border px-1.5 py-1.5 gap-1 ${navBarBg} shadow-lg shadow-black/20`}>
            <button
              type="button"
              onClick={() => {
                setTab('home');
                setSelectedModule(null);
                setShowQuickMenu(false);
              }}
              className="flex-1 flex flex-col items-center py-2 rounded-[24px] transition-colors"
              style={tab === 'home' ? { backgroundColor: navActiveBg } : undefined}
            >
              <NeoIonicons
                name="home-outline"
                size={20}
                color={tab === 'home' ? A.primary : navInactiveIcon}
                filled={tab === 'home'}
              />
              <span
                className="text-[10px] mt-0.5"
                style={
                  tab === 'home'
                    ? { color: A.primary, fontWeight: 700 }
                    : { color: navInactiveIcon, fontWeight: 500 }
                }
              >
                Home
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setTab('modules');
                setSelectedModule(null);
                setShowQuickMenu(false);
              }}
              className="flex-1 flex flex-col items-center py-2 rounded-[24px] transition-colors"
              style={tab === 'modules' ? { backgroundColor: navActiveBg } : undefined}
            >
              <NeoIonicons
                name="grid-outline"
                size={20}
                color={tab === 'modules' ? A.primary : navInactiveIcon}
                filled={tab === 'modules'}
              />
              <span
                className="text-[10px] mt-0.5"
                style={
                  tab === 'modules'
                    ? { color: A.primary, fontWeight: 700 }
                    : { color: navInactiveIcon, fontWeight: 500 }
                }
              >
                Modules
              </span>
            </button>
            <div className="w-12 shrink-0" />
            <button
              type="button"
              onClick={() => {
                setTab('scan');
                setSelectedModule(null);
                setShowQuickMenu(false);
              }}
              className="flex-1 flex flex-col items-center py-2 rounded-[24px] transition-colors"
              style={tab === 'scan' ? { backgroundColor: navActiveBg } : undefined}
            >
              <NeoIonicons
                name="scan-outline"
                size={20}
                color={tab === 'scan' ? A.primary : navInactiveIcon}
                filled={tab === 'scan'}
              />
              <span
                className="text-[10px] mt-0.5"
                style={
                  tab === 'scan' ? { color: A.primary, fontWeight: 700 } : { color: navInactiveIcon, fontWeight: 500 }
                }
              >
                Scan
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setTab('settings');
                setSelectedModule(null);
                setShowQuickMenu(false);
              }}
              className="flex-1 flex flex-col items-center py-2 rounded-[24px] transition-colors"
              style={tab === 'settings' ? { backgroundColor: navActiveBg } : undefined}
            >
              <NeoIonicons
                name="settings-outline"
                size={20}
                color={tab === 'settings' ? A.primary : navInactiveIcon}
                filled={tab === 'settings'}
              />
              <span
                className="text-[10px] mt-0.5"
                style={
                  tab === 'settings'
                    ? { color: A.primary, fontWeight: 700 }
                    : { color: navInactiveIcon, fontWeight: 500 }
                }
              >
                Settings
              </span>
            </button>
          </div>
          <button
            type="button"
            onClick={handleCenterPress}
            className="absolute left-1/2 -translate-x-1/2 -top-7 w-14 h-14 rounded-full flex items-center justify-center border-[3px] shadow-lg transition-transform active:scale-95"
            style={{
              backgroundColor: showQuickMenu ? '#ef4444' : A.primary,
              borderColor: showQuickMenu ? '#f87171' : A.primaryLight,
              boxShadow: showQuickMenu ? '0 8px 24px rgba(239,68,68,0.35)' : `0 8px 24px ${A.primary}55`,
            }}
            aria-label={showQuickMenu ? 'Close quick menu' : 'Quick actions'}
          >
            <NeoIonicons name={showQuickMenu ? 'close' : 'add'} size={26} color="#ffffff" filled />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {panel === 'profile' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setPanel(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0d0d0f] p-6 shadow-2xl"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">Profile</h3>
                  <p className="text-sm text-white/50 mt-1">Neo session</p>
                </div>
                <button type="button" onClick={() => setPanel(null)} className="p-2 rounded-xl hover:bg-white/10">
                  <NeoIonicons name="close-circle" size={28} color="rgba(255,255,255,0.45)" filled />
                </button>
              </div>
              <div className="flex flex-col items-center py-4">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center border"
                  style={{ backgroundColor: `${A.primary}33`, borderColor: `${A.primaryLight}66` }}
                >
                  <NeoIonicons name="person" size={36} color={A.primaryLight} filled />
                </div>
                <p className="text-lg font-bold text-white mt-4">LYNCS User</p>
                <p className="text-sm text-white/50">Operations Manager</p>
                <span className="mt-3 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400">ONLINE</span>
              </div>
            </motion.div>
          </motion.div>
        )}
        {panel === 'activity' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setPanel(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0d0d0f] p-6 max-h-[70vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Activity</h3>
                <button type="button" onClick={() => setPanel(null)} className="p-2 rounded-xl hover:bg-white/10">
                  <NeoIonicons name="close-circle" size={28} color="rgba(255,255,255,0.45)" filled />
                </button>
              </div>
              <div className="space-y-2">
                {RECENT_ACTIVITY.map((r) => (
                  <div key={r.id} className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-sm font-medium text-white">{r.title}</p>
                    <p className="text-xs text-white/45 mt-1">
                      {r.mod} · {r.time}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
        {panel === 'search' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setPanel(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0d0d0f] p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Search</h3>
                <button type="button" onClick={() => setPanel(null)} className="p-2 rounded-xl hover:bg-white/10">
                  <NeoIonicons name="close-circle" size={28} color="rgba(255,255,255,0.45)" filled />
                </button>
              </div>
              <p className="text-sm text-white/50">Search across Neo — connect your backend to mirror mobile Neo search.</p>
            </motion.div>
          </motion.div>
        )}
        {panel === 'customize' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setPanel(null)}
          >
            <motion.div
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 48, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0a0a0c] shadow-2xl overflow-hidden"
            >
              <div className="w-12 h-1.5 rounded-full bg-white/25 mx-auto mt-3 shrink-0" />
              <div className="flex justify-between items-center px-6 pt-4 pb-2 shrink-0">
                <h3 className="text-xl font-extrabold text-white">Home modules</h3>
                <button type="button" onClick={() => setPanel(null)} className="p-2 rounded-xl hover:bg-white/10">
                  <NeoIonicons name="close-circle" size={28} color="rgba(255,255,255,0.45)" filled />
                </button>
              </div>
              <p className="text-xs text-white/50 px-6 pb-4">
                Home uses the same six categories as the Neo mobile app (Supply Chain, Maintenance, Logistics, Assets,
                Inventory, Procurement). Open a category to see its actions.
              </p>
              <div className="flex-1 overflow-y-auto px-6 pb-8 space-y-2">
                {MODULES.map((cat) => (
                  <div
                    key={cat.neoId}
                    className="flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.04]"
                  >
                    <div className="w-[58px] h-[58px] shrink-0 flex items-center justify-center" style={neoVividChipStyle(cat.color, isDark)}>
                      <NeoIonicons name={cat.ionIcon} size={24} color={cat.color} filled />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[15px] font-semibold text-white block truncate">{cat.label}</span>
                      <span className="text-[11px] text-white/40">{cat.actions.length} actions</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
